/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    Output,
    TemplateRef,
    ViewChild,
    ViewChildren
} from '@angular/core';
import { ContentInjectionService, LogService, UtilsService } from '../../services';
import { DropdownModel } from '../../models';

@Component({
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => DropdownComponent),
        }
    ]
})
export class DropdownComponent implements ControlValueAccessor {
    /**
     * Get at the list item to scroll to them using native element.
     */
    @ViewChildren('listItem')
    public listItem: any;

    /**
     * Get drop hidden input.
     */
    @ViewChild('hiddenInput', { static: true })
    public hiddenInput: ElementRef;

    /**
     * Get drop container.
     */
    @ViewChild('dropContainer', { static: true })
    public dropContainer: ElementRef;

    /**
     * Template for dropdown content.
     */
    @ViewChild('dropTemplate', { static: true })
    public dropTemplate: TemplateRef<any>;

    /**
     * Get at the list item to scroll to them using native element.
     */
    @ViewChild('searchInput')
    public searchInput: any;

    /**
     * Store the reference to the dropdown toggle button.
     */
    @ViewChild('dropToggle')
    public dropToggle: ElementRef;

    /**
     * Start text for an empty selection.
     */
    @Input() public startText: string = '- Select -';

    /**
     * Always show the start text as a dropdown option.
     */
    @Input() public alwaysShowStartText: boolean = false;

    /**
     * No content text.
     */
    @Input() public noContentText: string = '- No Content -';

    /**
     * Dropdown ID for html
     */
    @Input() public dropDownId: string;

    /**
     * Content array of key/value pair objects to be shown in the dropdown.
     * @param content
     */
    @Input() set content(content: any) {
        if (this.alwaysShowStartText) {
            this.addStartTextToDropdown(content);
        }

        this._content = content;
        this._tempRows = content;
        this.writeValue(this.storedKey);
    }

    /**
     * List of content keys for the items to be hidden.
     * @param list
     */
    @Input() set hiddenContentKeys(list: any[]) {
        this._hiddenContentKeys = this.utilsService.cleanArray(list).filter(item => item !== this.startText);
    }

    /**
     * The dropdown width
     * @param width
     */
    @Input() set dropdownWidth(width: string | number) {
        this._dropdownWidth = DropdownComponent.setDropdownWidth(width);
    }

    /**
     * The dropdown width
     * @param width
     */
    @Input() set dropdownContentWidth(width: string | number) {
        this._dropdownContentWidth = DropdownComponent.setDropdownWidth(width);
    }

    /**
     * Our form control
     * @param controlName
     */
    @Input() set formControlName(controlName: string) {
        this._formControlName = controlName;
    }

    /**
     * Our form group
     * @param formGroup
     */
    @Input() set formGroup(formGroup: FormGroup) {
        this._formGroup = formGroup;
    }

    /**
     * Selected item index
     * @param key
     */
    @Input() set selectedKey(key: string | number) {
        // Set the value to null if the key is empty to meet conditions in writeValue
        this._selectedKey = key || null;
        this.writeValue(this._selectedKey);
        if (this.formControlName !== 'default' && key !== '') {
            this.logService.warn('Accordion Dropdown selectedKey',
                'You are setting a selected key and using a form control, you should NOT do both: form control name '
                + this.formControlName);
        }
    }

    /**
     * Selected item index
     * @param item
     */
    @Input() set preSelectedItem(item: DropdownModel) {
        if (item) {
            this.selectedItem = this.utilsService.duplicateObject(item);
            this.selectedText = item.value;
            this.storedKey = item.key;
        }
    }

    /**
     * Dropdown title alignment
     */
    @Input() public titleAlign: 'left' | 'center' | 'right' = 'left';

    /**
     * Title only use when the content is html and using an icon.
     */
    @Input() public selectedTitle: string;

    /**
     * HTML Title used for hover hint text.
     */
    @Input() public title: string;

    /**
     * Title only use when the content is html and using an icon.
     */
    @Input() public hintText: string = undefined;

    /**
     * The image you what to display.
     */
    @Input() public image: string;

    /**
     * Show ir hide the dropdown arrow.
     */
    @Input() public hideArrow: boolean = false;

    /**
     * Pass in a template reference.
     */
    @Input() public template: TemplateRef<any>;

    /**
     * Boolean value to determine if you want a search bar in the dropdown or not.
     */
    @Input() public search: boolean = false;

    /**
     * For ng-content based dropdowns should the title be changed by the selection in the dropdown
     */
    @Input() public titleBasedOnSelection: boolean = false;

    /**
     * Optional input to force the dropdown to open on load
     * @param state
     */
    @Input() set forceOpen(state: boolean) {
        this._forceOpen = state;
    }

    /**
     * Optional input to force the dropdown to be disabled
     * @param state
     */
    @Input() set disabled(state: boolean) {
        if (state) {
            this.formGroup.get(this.formControlName).disable();
        } else {
            this.formGroup.get(this.formControlName).enable();
        }

        this._disabled = state;
    }

    /**
     * Force the dropdown to always be enabled.
     * @param state
     */
    @Input() set alwaysEnabled(state: boolean) {
        this._alwaysEnabled = state;
    }

    /**
     *  Flag to be passed to html dropdowns with search functionality. Shows no content message if no matches.
     * @param matches
     */
    @Input() set matches(matches: boolean) {
        this._matches = matches;
    }

    /**
     * If the dropdown has no FormControl name, but we still want it to style is as an input set true.
     */
    @Input() public displayAsInput: boolean = false;

    /**
     * Store the background colour of the drop content.
     */
    @Input() public contentBackground: string = '#fff';

    /**
     * Invert the colours of the dropdown.
     */
    @Input() public invert: boolean = false;

    /**
     * Store the width of the drop content container.
     */
    @Input() public contentWidth: number;

    /**
     * Apply external styling
     */
    @Input() public applyExternalStyling: boolean = false;

    /**
     * Selected item that will be sent to the parent component.
     */
    @Output() public keyOut: EventEmitter<any> = new EventEmitter();

    /**
     * Selected object that will be sent to the parent component.
     */
    @Output() public selectedOut: EventEmitter<any> = new EventEmitter();

    /**
     * Search term that will be sent to the parent component.
     */
    @Output() public searchOut: EventEmitter<any> = new EventEmitter();

    /**
     * Dropdown state event that will be sent to the parent component.
     */
    @Output() public dropdownOpened: EventEmitter<boolean> = new EventEmitter();

    /**
     * Our selected key/value object
     */
    public selectedItem: DropdownModel = {
        key: '',
        value: ''
    };

    /**
     * Text displayed to the user as the dropdown title.
     */
    public selectedText: string;

    /**
     * Keyboard event
     */
    public highLightedItem: DropdownModel;

    /**
     * Search value store
     */
    public searchValue: any = '';

    /**
     * Open state of the dropdown
     */
    public open: boolean = false;

    /**
     * Fake disable to make the dropdown look disabled. We can't actually disable the dropdown as it causes issues with validation.
     */
    public fakeDisabled: boolean = false;

    /**
     * Container open direction.
     */
    public openUp: boolean = false;

    /**
     * Getter and Setter private variables.
     * @private
     */
    private _content: DropdownModel[];
    private _hiddenContentKeys: any[] = [];
    private _dropdownWidth: string | number;
    private _dropdownContentWidth: number | string;
    private _forceOpen: boolean = false;
    private _formControlName: string = 'default';
    private _formGroup: FormGroup = new FormGroup({ default: new FormControl('') });
    private _matches: boolean = false;
    private _selectedKey: any = null;
    private _tempRows: any;
    private _alwaysEnabled: boolean = false;
    private _disabled: boolean = false;

    /**
     * Counter for the list item index
     */
    private listIndexCounter: number = 0;

    /**
     * Stores form value as the key while waiting for content.
     * @private
     */
    private storedKey: any = null;

    /**
     * Store this instances event listener state.
     * @private
     */
    private hasEventListener: boolean = false;

    constructor(
        private cdr: ChangeDetectorRef,
        private contentInjectionService: ContentInjectionService,
        private logService: LogService,
        private utilsService: UtilsService
    ) {
        this.setupContentInjectionSubscriber();
    }

    /**
     * Get title text.
     */
    public getTitle(): string {
        return this.hintText ? '' : this.title ? this.title : this.selectedText ? this.selectedText : '';
    }

    /**
     * Add start text to dropdown if set.
     * @param content
     * @private
     */
    private addStartTextToDropdown(content: any[]): void {
        const match: DropdownModel = content.find((item: DropdownModel) => item.key === this.startText);
        if (!match) {
            content.unshift(<DropdownModel>{
                key: this.startText,
                value: this.startText
            });
        }
    }

    /**
     * Set the dropdown width.
     * @param width
     */
    private static setDropdownWidth(width: string | number): any {
        if (!width) {
            return 100 + '%';
        } else {
            return typeof width === 'number' ? width + 'px' : width;
        }
    }

    /**
     * Allow other on-key-press code to run, if the keycode is in the selected key array
     * or if the target is search and the key code is in the selected key array.
     * @param event
     * @param keyCode
     * @private
     */
    private static checkSearchKeyInputs(event: any, keyCode: string): boolean {
        const selectedKeys: string[] = ['Enter', 'ArrowDown', 'ArrowUp', 'Tab', 'Escape'];

        return selectedKeys.includes(keyCode) ||
            (event.relatedTarget && !event.relatedTarget.classList.contains('dropdown-search') && selectedKeys.includes(keyCode));
    }

    get formGroup() {
        return this._formGroup;
    }

    get formControlName() {
        return this._formControlName;
    }

    get content() {
        return this._content;
    }

    get hiddenContentKeys() {
        return this._hiddenContentKeys;
    }

    get dropdownWidth() {
        return this._dropdownWidth;
    }

    get dropdownContentWidth() {
        return this._dropdownContentWidth;
    }

    get selectedKey() {
        return this._selectedKey;
    }

    get forceOpen(): boolean {
        return this._forceOpen;
    }

    get matches(): boolean {
        return this._matches;
    }

    get alwaysEnabled(): boolean {
        return this._alwaysEnabled;
    }

    get disabled(): boolean {
        return this._disabled;
    }

    /**
     * Searches through the vips by search name
     * @param event
     */
    public searchTempRows(event: any): void {
        if (event.key === 'Tab') {
            this.exitDropdown();
        }

        // Reset the content when user deletes part of the search term, so it matches the new term if possible.
        if (event.key === 'Backspace' || event.key === 'Delete') {
            this._content = this._tempRows;
        }

        const val = event.target.value;
        if (val?.length) {
            this.searchValue = val;

            if (this.content?.length) {
                const regex: RegExp = new RegExp(val, 'gi');
                const results: any[] = [];
                // filter our data
                for (const r of this._tempRows) {
                    const row: any = r;
                    if (row.key?.toString().match(regex)
                        || row.value?.toString().match(regex)
                        || !val) {
                        results.push(row);
                    }
                }
                // update the rows
                this._content = results;
            } else {
                this.searchOut.next(val);
            }
        } else {
            this.clearSearch();
        }
    }

    /**
     * Clear the search input and reset the current content
     */
    public clearSearch(): void {
        // Empty our search
        if (this.searchInput) {
            this.searchInput.nativeElement.value = '';
            this.searchValue = '';
            this._content = this._tempRows;
            this.searchOut.next('');
        }
    }

    /**
     * Event handler for when the view changes
     * Note: Required by implementation. Do not remove.
     */
    public registerOnChange(): void {
        if (this.hiddenContentKeys?.length + 1 === this.content?.length) {
            const lastOption = this.content.filter((item: DropdownModel) => !this.hiddenContentKeys.includes(item.key));
            this.writeValue(lastOption[0].key);
        }
    }

    /**
     * Register a function to determine if the input has been touched
     * Note: Required by implementation. Do not remove.
     */
    public registerOnTouched(): void {
    }

    /**
     * Write value to input
     * Note: Required by implementation. Do not remove.
     * @param key
     */
    public writeValue(key: any): void {
        this.storedKey = key;
        this.fakeDisabled = false;

        // There is only one item in the content.
        if (key === null && this.content?.length === 1) {
            this.setSelectedItem(this.content[0]);
            this.setSelectedText(0);
            this.fakeDisabled = true;
            // There is a key, and it is not the selected key and there is content
        } else if (this.keyIsZero(key) && key !== this.selectedItem.key && this.content?.length) {
            // Set our selected item
            const index = this.content.findIndex((item: DropdownModel) => item.key === key);
            this.setSelectedItem(this.content[index]);
            this.setSelectedText(index);
            this.fakeDisabled = !this.alwaysShowStartText && this.content?.length === 1;
        } else if (key === null && this.selectedItem.key !== undefined && (this.content?.length > 1)) {
            this.selectedItem.key = key;
            this.selectedItem.value = key;
            this.setSelectedText(-1);
        } else if (key === null && (this.content && !this.content.length)) {
            this.setSelectedText(-1);
            this.fakeDisabled = true;
        } else if (key && key === this.selectedItem.key && this.content?.length) {
            this.fakeDisabled = !this.alwaysShowStartText && this.content?.length === 1;
            if (this.hiddenContentKeys?.length) {
                this.fakeDisabled = this.hiddenContentKeys.length + 1 === this.content?.length;
            }
        }
    }

    /**
     * Toggle the open the dropdown
     * @param $event
     */
    public toggleDrop($event: any): void {
        if (
            (!this.alwaysEnabled || !this.alwaysShowStartText) &&
            (this.fakeDisabled || this.formGroup.get(this.formControlName).disabled)
        ) {
            return;
        }

        if (this.content && this.hiddenContentMatchesContent()) {
            return;
        }

        this.open = !this.open;
        this.openUp = this.openUpwards();
        this.clearSearch();

        if (this.open) {
            this.dropdownOpened.emit();
            const overlayContentState: any = this.createOverlayContentState($event);
            this.contentInjectionService.openEmitter.next(overlayContentState);
        } else {
            this.contentInjectionService.closeEmitter.next(true);
        }
        this.cdr.detectChanges();
    }

    /**
     * Determine if the dropdown should open upwards.
     */
    public openUpwards(): boolean {
        const dropdownObj: DOMRect = this.dropContainer.nativeElement.getBoundingClientRect();
        const dropdownPos: number = dropdownObj.y + dropdownObj.height;
        const componentFooterVisible: Element[] = Array.from(document.getElementsByClassName('component-footer'));
        const componentFooterHeight: number = componentFooterVisible.length ? 96 : 0;
        const searchBarHeight: number = this.search ? 40 : 0;
        const containerHeight: number = (dropdownPos + componentFooterHeight + searchBarHeight + 180);

        return containerHeight > window.innerHeight;
    }

    /**
     * Select a list item, and it gets set to the title/where the placeholder is
     * @param item
     */
    public setSelectedItem(item: DropdownModel): void {
        // @ts-ignore
        if (item && !item.options?.disabled) {
            this.exitDropdown();

            // Set the selected item but duplicate the item first to fix any reference issue.
            this.selectedItem = this.utilsService.duplicateObject(item);

            // Mark the control as touched
            this.formGroup.controls[this.formControlName].markAsTouched();

            // Set the hidden field value and class touched for form validation.
            if (this.formGroup.controls[this.formControlName].value !== item.value) {
                this.formGroup.controls[this.formControlName].setValue(item.key);
            }

            this.selectedText = item.value;

            // Send the key the parent component wants to the component
            this.keyOut.emit(item.key);
            this.selectedOut.emit(item);
            this.contentInjectionService.closeEmitter.next(true);
        }
    }

    /**
     * Used in the HTML to set the placeholder/value text in the title.
     * @param index
     */
    public setSelectedText(index: number): void {
        // There is no content.
        if (!this.content?.length) {
            this.selectedText = this.noContentText;

            return;
        }

        // There is no selected key and no index show the default text.
        if (this.selectedItem && !this.selectedItem.key && !this.selectedItem.value) {
            this.selectedText = this.startText;

            return;
        }

        // There is only 1 item in the content set it as the title.
        if (this.content?.length === 1) {
            this.selectedText = this.content[0].value;

            return;
        }

        // Check to see if we have a selected item/key set.
        if (this.selectedItem?.key && this.content?.length && index) {
            this.selectedText = this.content[index]?.value;

            return;
        }

        this.selectedText = this.selectedItem?.value;
    }

    /**
     * Check if dropdown should be disabled.
     */
    public dropdownIsDisabled(): boolean {
        if (this.alwaysEnabled || this.alwaysShowStartText) {
            return false;
        }

        return this.fakeDisabled || (this.hiddenContentMatchesContent());
    }

    /**
     * Check hidden content against content.
     */
    public hiddenContentMatchesContent(): boolean {
        return this.content?.length - this.hiddenContentKeys?.length < (this.alwaysShowStartText ? 1 : 2);
    }

    /**
     * Document level click handler. Toggles open state of the dropdown, captures clicks outside the dropdown so only
     * one can be open at a time.
     * @param targetElement
     */
    @HostListener('document:click', ['$event.target'])
    public onClick(targetElement: HTMLElement): void {
        const formControlName: AbstractControl = this.formGroup.get(this.formControlName);
        const classList: string[] = Array.from(targetElement.classList);
        let classSearch: any[] = [];

        if (Array.from(classList).includes('overlay')) {
            this.contentInjectionService.closeEmitter.next(true);
        }

        if (this.hiddenContentMatchesContent()) {
            this.exitDropdown();

            return;
        }

        if (this.forceOpen) {
            this.open = true;

            return;
        }

        classSearch = ['clear-search-img', 'clear-search-img', 'clear-search-img'];
        if (classList.some(match => classSearch.includes(match))) {
            return;
        }

        // if form group is disabled don't open the dropdown.
        if (formControlName.disabled) {
            this.exitDropdown();

            return;
        }

        if (Array.from(classList).includes('dropItem')) {
            if (this.titleBasedOnSelection) {
                this.selectedTitle = targetElement.textContent;
            }
            this.keyOut.next(targetElement.textContent);
            this.exitDropdown();
        }

        classSearch = ['fake-disabled', 'list-content-wrapper', 'search-wrapper'];
        if (classList.some(match => classSearch.includes(match))) {
            this.contentInjectionService.closeEmitter.next(false);
        }
    }

    /**
     * When this instance of the input get focus add an event handler to the drop container.
     */
    public onFocus(): void {
        const nativeElement = this.utilsService.getObjectValue(this.dropContainer, 'nativeElement');
        if (nativeElement && !this.hasEventListener) {
            nativeElement.addEventListener('keydown', (event: any) => {
                this.hasEventListener = true;
                this.onKeyPress(event);
            });
        }
    }

    /**
     * Close dropdown when the hidden input looses focus and remove the event handler.
     * @param event
     */
    public closeOnBlur(event: any): void {
        if (event.relatedTarget && !event.relatedTarget.classList.contains('dropdown-search')) {
            this.exitDropdown();
        }
    }

    /**
     * Create the dropdown state.
     * @param $event
     * @private
     */
    private createOverlayContentState($event: any): any {
        const rect = this.dropToggle.nativeElement.getBoundingClientRect();

        return {
            id: 'dropdown',
            open: this.open,
            template: this.dropTemplate,
            anchorWidth: rect.width,
            containerWidth: this.getContentContainerWidth(),
            top: rect.top + 36,
            bottom: rect.bottom - 38,
            left: rect.left,
            event: $event,
            overlay: true,
            showCaret: false,
            overlaySelector: 'drop-content-container',
            extras: { 'background': this.contentBackground }
        };
    }

    /**
     * Set the width of the drop content container.
     * @private
     */
    private getContentContainerWidth(): any {
        if (this.contentWidth) {
            return this.contentWidth;
        } else if (this.dropdownWidth) {
            return this.dropdownWidth;
        } else {
            return this.dropContainer.nativeElement.offsetWidth;
        }
    }

    /**
     * Set dropdown to a closed state
     * @private
     */
    private exitDropdown(): void {
        this.open = false;
        this.listIndexCounter = 0;
        this.highLightedItem = { key: '', value: '' };
        this.clearSearch();
        this.searchOut.next('');
    }

    /**
     * Handle each individual key event.
     * @param event
     */
    private onKeyPress(event: any): void {
        const keyCode: string = event.key;

        if (this.open && DropdownComponent.checkSearchKeyInputs(event, keyCode)) {
            // Stop the page from scrolling.
            event.preventDefault();
            event.stopPropagation();

            switch (keyCode) {
                case 'ArrowDown':
                    this.handleDownArrowKey();
                    break;
                case 'ArrowUp':
                    this.handleUpArrowKey();
                    break;
                case 'Enter':
                    this.handleEnterKey();
                    break;
                case 'Tab':
                    this.handleTabKey();
                    break;
                case 'Escape':
                    this.toggleDrop(event);
                    break;
            }
            // Open the dropdown if it has been tabbed to and the down key is pressed.
        } else if (!this.open && keyCode === 'ArrowDown') {
            if (this.content?.length) {
                // Set the highlighted item to the first item in the list
                this.highLightedItem = this.content[0];
            }

            this.open = true;
        } else if (keyCode === 'Enter' && !this.open) {
            this.toggleDrop(event);
        }
    }

    /**
     * If the dropdown is open, and we press the down key we go down the list of items.
     * @private
     */
    private handleDownArrowKey(): void {
        // Check to make sure our counter is in range
        if (this.content && (this.listIndexCounter === this.content.length - 1)) {
            this.listIndexCounter = 0;
        } else {
            // Increase our position counter
            this.listIndexCounter++;
        }

        // If the new item is the selected item it is hidden in the dropdown and needs to be skipped.
        if (this.content[this.listIndexCounter].key === this.selectedItem.key) {
            this.listIndexCounter++;
        }

        // Set our highlighted item
        this.highlightSelectedItem();
    }

    /**
     * If the dropdown is open, and we press the up key we go up the list of items.
     * @private
     */
    private handleUpArrowKey(): void {
        // Check to see if we are in range
        if (this.content && this.listIndexCounter === 0) {
            // set our counter to be our content length
            this.listIndexCounter = this.content.length - 1;
        } else {
            // Increase our position counter
            this.listIndexCounter--;
        }

        // If the new item is the selected item it is hidden in the dropdown and needs to be skipped.
        if (this.content[this.listIndexCounter].key === this.selectedItem.key) {
            this.listIndexCounter--;
        }

        this.highlightSelectedItem();
    }

    /**
     * If the dropdown is open, and you press the enter key it set the highlighted item.
     * @private
     */
    private handleEnterKey(): void {
        // set our selected item
        if (this.content?.length) {
            this.setSelectedItem(this.content[this.listIndexCounter]);
        }
        this.exitDropdown();
    }

    /**
     * If the dropdown is open, and we press the tab key we close the dropdown and reset our counter
     * @private
     */
    private handleTabKey(): void {
        this.exitDropdown();
    }

    /**
     * Set the highlight selected item.
     * @private
     */
    private highlightSelectedItem(): void {
        if (this.content?.length > 3) {
            // Set our highlighted item
            this.highLightedItem = this.content[this.listIndexCounter];
            // Scroll to item
            this.scrollToHighlightedElement();
        }
    }

    /**
     * Scroll to the highlighted dropdown element
     * @private
     */
    private scrollToHighlightedElement(): void {
        const listElements = this.listItem.map((item: { nativeElement: any; }) => item.nativeElement);
        // Scroll within the parent container if the element is out of sight.
        if (listElements && listElements[this.listIndexCounter]) {
            // Note scrollIntoViewIfNeeded is a non-standard feature.
            listElements[this.listIndexCounter].scrollIntoViewIfNeeded(true);
        }
    }

    /**
     * Setup content injection subscriber.
     * @private
     */
    private setupContentInjectionSubscriber(): void {
        this.contentInjectionService.closeEmitter
            .subscribe({
                next: (closed: boolean): void => {
                    this.open = !closed;
                    if (!this.open) {
                        this.exitDropdown();
                    }
                }
            });
    }

    /**
     * Check if key is zero as zero is valid if it is a number.
     * @param key
     * @private
     */
    private keyIsZero(key: any): boolean {
        if (typeof key === 'number') {
            return key > -1;
        } else {
            return !!key;
        }
    }
}
